/**
 * The room, as walls.
 *
 * A room is a closed loop of straight walls, in millimetres, on the floor. That is all it is
 * -- there is no "width and depth" any more, because the moment a kitchen has a splayed
 * corner or a nook for the fridge those two numbers stop describing it, and most kitchens
 * have one or the other.
 *
 * Coordinates are the SCENE's: x to the right, and the second axis is three.js's z, which
 * increases TOWARD the viewer. So the back wall of a room sits at negative z and the front at
 * zero, and on a plan drawn with z downwards the walls run CLOCKWISE. That winding is not a
 * convention to be tidy about: inward is to the right of travel along every wall, and every
 * run, benchtop and door face is placed by it. `clockwise` enforces it.
 *
 * Any angle. A wall at 45 degrees is a wall; a run along it is a run. The only place an angle
 * costs anything is the corner, where two runs of the same depth meet and would overlap by
 * more or less than they do at ninety -- see `allowance`.
 *
 * Pure. No three.js, no Vue.
 */

export const dir = (w) => {
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1
  const len = Math.hypot(dx, dy) || 1
  return { x: dx / len, y: dy / len }
}
export const length = (w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1)
/** Rotation about the vertical that sends local +x along the wall. */
export const angle = (w) => Math.atan2(-(w.y2 - w.y1), w.x2 - w.x1)
/** Into the room: the direction to the right of travel, for a clockwise loop. */
export const inward = (w) => { const d = dir(w); return { x: -d.y, y: d.x } }
export const at = (w, t) => { const d = dir(w); return { x: w.x1 + d.x * t, y: w.y1 + d.y * t } }

/**
 * Signed shoelace area. POSITIVE is clockwise on a plan drawn with y downward -- the sign is
 * the opposite of the textbook's because the textbook's y points up. Worth a test, and
 * there is one.
 */
export function area(walls) {
  let a = 0
  for (const w of walls) a += (w.x1 * w.y2 - w.x2 * w.y1)
  return a / 2
}

/**
 * The same loop, wound clockwise. Reversing a loop swaps each wall's ends and the order of
 * the walls, and keeps every id, so a run attached by wallId still means the same wall.
 */
export function clockwise(walls) {
  if (area(walls) >= 0) return walls
  return walls.slice().reverse().map((w) => ({ ...w, x1: w.x2, y1: w.y2, x2: w.x1, y2: w.y1 }))
}

/** The wall after this one in the loop, sharing its end point. */
export const next = (walls, w) => walls[(walls.indexOf(w) + 1) % walls.length]
export const prev = (walls, w) => walls[(walls.indexOf(w) - 1 + walls.length) % walls.length]

/**
 * The interior angle at the corner where wall `a` ends and wall `b` begins, in radians.
 *
 * Ninety degrees for an ordinary corner. Less for a sharp one, more for a splay, and 180 for
 * two walls that are really one -- which is what a notch or a door opening leaves behind.
 */
export function corner(a, b) {
  const da = dir(a), db = dir(b)
  // turn from a's heading to b's; clockwise loop turns clockwise at every convex corner
  const cross = da.x * db.y - da.y * db.x
  const dot = da.x * db.x + da.y * db.y
  const turn = Math.atan2(cross, dot)
  return Math.PI - turn
}

/**
 * How far along each leg a corner is spoken for.
 *
 * Two runs of depth `d` meeting at interior angle `theta` overlap, measured along either
 * wall from the vertex, by d / tan(theta / 2). Ninety degrees gives d itself, which is why
 * the built app could get away with a constant. A splayed 135 gives less than half that; a
 * sharp 60 gives nearly twice.
 */
export function allowance(d, theta) {
  const t = Math.tan(theta / 2)
  /*
   * A CORNER THAT TURNS THE OTHER WAY COSTS NOTHING, and getting that wrong cost the owner
   * their sink wall.
   *
   * `corner` returns an interior angle anywhere in (0, 360). Past 180 the two walls bend
   * away from each other -- the inside corner of an L-shaped floor, or a scan that split one
   * long wall into two pieces meeting at 182.5 degrees -- and two runs on them diverge
   * rather than overlap, so neither is standing on the other's floor and neither owes the
   * corner anything.
   *
   * `tan(theta / 2)` goes NEGATIVE past 180, and a negative number is less than 0.05, so
   * every one of those corners fell through the hairpin guard below and claimed twenty times
   * the cabinet depth. On the owner's scan that was an 11-metre claim on an 8.26-metre wall:
   * `extents` returned {from: 8260, to: 8260} and the wall carrying their sink and their
   * dishwasher was handed back with no room for a single cabinet.
   */
  if (t <= 0) return 0
  if (t < 0.05) return d * 20   // effectively a hairpin; nothing fits there
  return Math.round(d / t * 10) / 10   // to a tenth: nobody cuts to a femtometre
}

/**
 * How much of the second wall the corner is actually spoken for.
 *
 * `allowance` above is the bare geometric overlap of two runs of the same depth meeting at
 * an angle -- 560mm at a right angle, which is exactly enough that the two runs do not
 * stand on the same piece of floor. It is the right number for two runs of plain cabinets.
 *
 * It is the wrong number for a corner UNIT. A real corner cabinet -- a carousel, a blind
 * corner, an L -- reaches along BOTH walls, typically 900 on each, so the run coming into
 * the corner has to start past its return leg rather than merely past the overlap. Without
 * this the corner unit is drawn as a straight 900 box tiling exactly into the allowance,
 * which is what the app did: correct in the schedule, correct in the review, and wrong in
 * every picture.
 *
 * A maker who does not stock one leaves `cornerReturn` out and gets exactly the old number,
 * which is what keeps every existing test and every existing kitchen unchanged.
 */
export const cornerClaim = (lim, d, theta) => Math.max(allowance(d, theta), lim?.cornerReturn ?? 0)

/** Axis-aligned bounds of the loop. */
export function bounds(walls) {
  const xs = walls.flatMap((w) => [w.x1, w.x2])
  const ys = walls.flatMap((w) => [w.y1, w.y2])
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) }
}

/** The loop's vertices in order, for drawing. */
export const vertices = (walls) => walls.map((w) => ({ x: w.x1, y: w.y1 }))

// ── presets ────────────────────────────────────────────────────────────────────────────

/**
 * The shapes most kitchens are, as a starting point.
 *
 * Every one is wound clockwise with the back wall first, at negative z, so the camera's idea
 * of "the back of the room" survives a change of shape. Ids are stable across presets so a
 * run attached to 'back' stays attached to the back.
 */
export function preset(shape, o = {}) {
  const W = o.width ?? 4200
  const D = o.depth ?? 3600
  const loop = (pts) => pts.map(([x, y], i) => {
    const [x2, y2] = pts[(i + 1) % pts.length]
    return { id: o.ids?.[i] ?? 'w' + i, x1: x, y1: y, x2, y2 }
  })
  const named = (pts, ids) => clockwise(loop(pts)).map((w, i) => ({ ...w, id: ids[i] ?? w.id }))

  switch (shape) {
    case 'single':
    case 'galley':
    case 'l':
    case 'u':
    case 'box':
    default:
      // A box, in every case: the shape is which walls CARRY RUNS, not the room's outline.
      // A galley is a box with runs on two facing walls; an L is a box with runs on two that
      // meet. The outline only changes when a wall is dragged or a corner added.
      return named([[0, -D], [W, -D], [W, 0], [0, 0]], ['back', 'right', 'front', 'left'])
  }
}

/** Which walls a preset puts cabinets on, and what kind. */
export function presetRuns(shape) {
  switch (shape) {
    case 'single': return [['back', 'base'], ['back', 'wall']]
    case 'galley': return [['back', 'base'], ['back', 'wall'], ['front', 'base']]
    case 'u':      return [['back', 'base'], ['back', 'wall'], ['left', 'base'], ['left', 'wall'],
                           ['right', 'base'], ['right', 'wall']]
    case 'l':
    default:       return [['back', 'base'], ['back', 'wall'], ['left', 'base'], ['left', 'wall']]
  }
}

// ── runs on walls ───────────────────────────────────────────────────────────────────────

/**
 * Where each run may actually put cabinets, once corners have been paid for.
 *
 * A run that meets another run of the same kind at a vertex gives up `allowance` at that
 * end. Only the SECOND run around the corner gives way, so the first keeps its full wall
 * and the corner's slot belongs to it -- which is where the corner cabinet will be placed
 * when the maker's range has one.
 *
 * Returns a map of run id to { from, to } in millimetres along the wall.
 */
// ── segments: the lines a run can stand on ─────────────────────────────────────────────
//
// A run used to be resolved with `plan.walls.find(x => x.id === run.wallId)`, in seven
// places. An island is a run that is not on a wall of the room, and three of those seven
// answered a missing wall with something plausible instead of nothing: `roomXY` in
// guidelines.js returned `{ x: along, y: 0 }` and quietly moved the sink to the origin, and
// `extents` below called `prev(walls, seg)`, which for a segment that is not in the array
// indexes with -1 and hands back a real wall, a real corner angle, and a wrong allowance
// that looks right.
//
// So resolution goes through one place. `segments` is the room's walls plus whatever
// islands the plan has; `segment` is the lookup; `isIsland` is the question the two or
// three places that must behave differently ask.

/** Every straight line a run can stand on: the room's walls, and any islands. */
export const segments = (plan) => (plan.islands?.length ? plan.walls.concat(plan.islands) : plan.walls)

/** The segment a run is on, or null. Never guesses. */
export const segment = (plan, id) => segments(plan).find((s) => s.id === id) ?? null

/** True when this segment is an island: free-standing, so it has no corner and no neighbour. */
export const isIsland = (plan, id) => !!plan.islands?.some((s) => s.id === id)

/**
 * A free-standing run of cabinets, as a segment.
 *
 * An island is a wall-shaped thing that is not part of the room's loop, which is the whole
 * trick: `dir`, `length`, `angle`, `inward` and `at` are functions of two endpoints and do
 * not care whether the endpoints close a polygon, so every one of them works on an island
 * unchanged. Only the LOOKUP had to widen, and `segments` above is that.
 *
 * WHICH WAY IT FACES is its own direction. Cabinets stand on the `inward` side -- to the
 * right of travel, the same convention the room's walls use -- and the seating overhang on
 * the other. There is no "inside" for an island to face, so the segment's direction is the
 * only thing that can decide, and flipping it is swapping its two ends.
 *
 * It is deliberately NOT in `plan.walls`: `area`, `clockwise`, `vertices`, `bounds`, `next`
 * and `prev` all assume a closed loop, and so does the floor. An island in there breaks the
 * room.
 */
export function island(centre, angleRad, len, opts = {}) {
  const half = len / 2
  const dx = Math.cos(angleRad), dy = -Math.sin(angleRad)
  return {
    id: opts.id ?? 'island-1',
    x1: Math.round(centre.x - dx * half), y1: Math.round(centre.y - dy * half),
    x2: Math.round(centre.x + dx * half), y2: Math.round(centre.y + dy * half),
    overhang: opts.overhang ?? 300,
    carries: opts.carries ?? null,
  }
}

/** The middle of a segment. */
export const middle = (s) => ({ x: (s.x1 + s.x2) / 2, y: (s.y1 + s.y2) / 2 })

/** Moved bodily, both ends together. */
export const moveSegment = (s, dx, dy) => ({
  ...s, x1: Math.round(s.x1 + dx), y1: Math.round(s.y1 + dy),
  x2: Math.round(s.x2 + dx), y2: Math.round(s.y2 + dy),
})

/** Turned about its own middle, so it spins where it stands. */
export function spinSegment(s, deg) {
  const c = middle(s)
  const a = -deg * Math.PI / 180        // screen y is down, so a positive turn is clockwise
  const cos = Math.cos(a), sin = Math.sin(a)
  const turn = (x, y) => ({
    x: Math.round(c.x + (x - c.x) * cos - (y - c.y) * sin),
    y: Math.round(c.y + (x - c.x) * sin + (y - c.y) * cos),
  })
  const p = turn(s.x1, s.y1), q = turn(s.x2, s.y2)
  return { ...s, x1: p.x, y1: p.y, x2: q.x, y2: q.y }
}

/** Swap the ends, which turns the island round: cabinets one side, seating the other. */
export const flipSegment = (s) => ({ ...s, x1: s.x2, y1: s.y2, x2: s.x1, y2: s.y1 })

/**
 * Every side of every base run a person can stand in front of, and how far it projects.
 *
 * The walkway rule used to pair RUNS and subtract two bench fronts, which is right when
 * every run is against a wall and has one usable face. An island has two, with different
 * depths: the bench front on the cabinet side, and the seating overhang on the other. So
 * the rule pairs FACES, and the same list draws the clearance bands on the plan.
 */
export function faces(plan, lim) {
  const out = []
  const front = lim.baseDepth + lim.benchtopOverhang
  for (const run of plan.runs.filter((r) => r.kind === 'base')) {
    const s = segment(plan, run.wallId)
    if (!s) continue
    const n = inward(s)
    const mid = at(s, length(s) / 2)
    out.push({ run, seg: s, p: mid, n, front, side: 'front' })
    if (isIsland(plan, run.wallId)) {
      const over = s.overhang ?? 0
      // The seating side. An island with no overhang still has a back, and a person still
      // has to walk past it, so the face exists with a front of zero.
      out.push({ run, seg: s, p: mid, n: { x: -n.x, y: -n.y }, front: over, side: 'back' })
    }
  }
  return out
}

// ── openings: the doors and windows a run has to live with ─────────────────────────────
//
// An opening is declared on a wall, at a distance along it, with a width, a sill and a
// height. It is the record; nothing else is. A cabinet does not "know" it is next to a
// doorway, and the gap a run leaves for one is a CONSEQUENCE that is recomputed, never a
// thing stored -- which matters because `refit` silently deletes an item whose specId it
// does not recognise, so a gap kept as data would vanish the first time somebody resized
// the room and nobody would ever know why the fridge ended up across the door.

/**
 * What a door and a window are when the drawing does not say.
 *
 * The AI reader gives a width and a position and no heights at all; a LiDAR scan gives real
 * ones. These fill the gap, and the confirm list shows them so an assumption is visible
 * before it is applied. 2040 is the standard Australian door leaf; 900 is the sill height
 * that puts a window on top of a benchtop, which is where kitchen windows go.
 */
export const OPENING = {
  door: { sill: 0, height: 2040 },
  window: { sill: 900, height: 1200 },
}

/**
 * How far below the benchtop a sill has to be before it is in the way.
 *
 * A kitchen window sills at 900 and a benchtop tops out at 903, so measured exactly, every
 * standard window in the country is three millimetres into the bench -- and the first
 * version of this rule duly shortened the run for one, and told the owner their window was
 * a problem. It is not: the stone runs up to the sill, which is what everyone builds. Fifty
 * millimetres is the difference between "on the bench" and "through it".
 */
const SILL_SLACK = 50

/**
 * A stretch of wall shorter than this holds no cabinet, so it is not worth keeping.
 *
 * A doorway 140mm from the corner leaves 140mm of run in front of it: too narrow for the
 * smallest cabinet in anybody's range, too wide to ignore, and enough to send the generator
 * hunting for a layout that satisfies a rule it cannot satisfy. Better to give the strip to
 * the doorway and start the run past it, which is what a person would draw.
 */
const SLIVER = 400

/** The shortest run worth having: one cabinet and a filler either side of it. */
export const MIN_RUN = 400

/** Where an opening starts and ends along its wall. */
export const spans = (o) => ({ from: o.at, to: o.at + o.width })

/**
 * Is this opening in the way of a run of this kind?
 *
 * The whole vertical rule, in one place. A base run occupies the floor to the top of the
 * benchtop; an overhead run hangs above the splashback. So a window on a 900 sill is NOT a
 * problem for the cabinets under it -- it sits on the bench, which is exactly what everyone
 * builds -- and IS a problem for an overhead crossing it. A door blocks both.
 */
export function blocks(o, kind, lim) {
  if (!o) return false
  const sill = o.sill ?? OPENING[o.kind]?.sill ?? 0
  const height = o.height ?? OPENING[o.kind]?.height ?? 2040
  const top = sill + height
  if (kind === 'wall') {
    const shelfBottom = lim.kickHeight + lim.baseHeight + lim.benchtopThickness + lim.splashbackHeight
    return top > shelfBottom + 1 && sill < shelfBottom + lim.wallHeight - 1
  }
  // base and tall both stand on the floor.
  const benchTop = lim.kickHeight + lim.baseHeight + lim.benchtopThickness
  return sill < benchTop - SILL_SLACK
}

/** The openings on one wall that are in the way of a run of this kind, in order. */
export function blocking(plan, wallId, kind, lim) {
  return (plan.openings ?? [])
    .filter((o) => o.wallId === wallId && blocks(o, kind, lim))
    .sort((a, b) => a.at - b.at)
}

/**
 * The same, as the stretches of wall a run may not stand in -- OVERLAPS MERGED.
 *
 * Two openings that overlap are two records and ONE hole, and every place that turns an
 * opening into a `gap` item had been counting them separately: the run then spent both
 * widths on the same stretch of wall and came out longer than the wall, which is reported as
 * "adds up to 7283 on a 6433 leg" and rejects the whole design.
 *
 * A scan produces overlapping openings routinely and honestly. RoomPlan reports a wide cased
 * opening as an `opening` and often the door leaf inside it as a `door` as well; two pieces
 * of one archway come back as two; and `readOpenings` snaps each to ten millimetres against
 * the wall it is nearest, which can slide two neighbours into each other. None of that is a
 * fault to be reported -- they are the same hole -- so they are merged where the hole is
 * used, and left alone where the openings themselves are (the plan draws each one, and the
 * review talks about them one at a time).
 *
 * @returns {Array<{from, to, kind, ids: string[]}>} in order, none overlapping
 */
export function blockedSpans(plan, wallId, kind, lim) {
  const out = []
  for (const o of blocking(plan, wallId, kind, lim)) {
    const s = spans(o)
    const last = out[out.length - 1]
    if (last && s.from <= last.to + 0.5) {
      last.to = Math.max(last.to, s.to)
      last.ids.push(o.id)
      // A door and a window in one hole is a door: it goes to the floor, which is the
      // stricter of the two and the one everything downstream has to respect.
      if (o.kind === 'door') last.kind = 'door'
    } else out.push({ from: s.from, to: s.to, kind: o.kind, ids: [o.id] })
  }
  return out
}

export function extents(plan, lim) {
  const walls = plan.walls
  const out = {}
  const depthOf = (kind) => (kind === 'wall' ? lim.wallDepth : lim.baseDepth)
  for (const run of plan.runs) {
    const w = segment(plan, run.wallId)
    if (!w) { out[run.id] = { from: 0, to: 0 }; continue }
    const L = length(w)
    // An island stands on its own: no wall before it, so no corner to give way to. Said
    // here explicitly rather than left to fall through, because `prev()` below would answer
    // a segment that is not in the loop with a real wall and a plausible wrong allowance.
    if (isIsland(plan, run.wallId)) { out[run.id] = { from: 0, to: L }; continue }
    // A run FILLS its wall. The only thing that shortens it is a run of the same kind on the
    // wall before, which reaches this vertex first and keeps the corner; we start past it.
    // Computed from the walls every time rather than read back off the run, so a corner
    // whose angle has just changed under a drag is paid for at its new angle, not its old.
    const before = prev(walls, w)
    const other = plan.runs.find((r) => r.kind === run.kind && r.wallId === before.id && r !== run)
    // A base run gives way to the corner UNIT's return leg; an overhead only to the bare
    // geometric overlap, because there is no corner overhead in anybody's range.
    let from = !other ? 0
      : run.kind === 'base' ? cornerClaim(lim, depthOf(run.kind), corner(before, w))
        // An overhead corner claims too, where the maker stocks one. Same shape of rule as
        // the base's, and the same guard: no `wallCornerReturn` means the bare overlap,
        // which is what every maker got before there was a corner wall unit to put there.
        : Math.max(allowance(depthOf(run.kind), corner(before, w)), lim?.wallCornerReturn ?? 0)
    let to = L

    /*
     * A doorway at either end of the wall shortens the run.
     *
     * Which is where doorways nearly always are, and it is the only case that can be
     * answered by moving one number. An opening in the MIDDLE of a wall cannot: the run is
     * one contiguous list of cabinets whose positions are implied by their order, so there
     * is nowhere to put a hole. That case is handled where the cabinets are chosen
     * (design.js) and caught where the design is judged (guidelines.js), and this stays
     * exactly one interval -- which is what lets `faults`, `refit`, `reflow`, the plan and
     * the renderer all carry on believing what they already believe.
     */
    for (const o of blocking(plan, w.id, run.kind, lim)) {
      const s = spans(o)
      // At the start, or near enough that what is left in front of it is unusable.
      if (s.from <= from + SLIVER) from = Math.max(from, s.to)
      else if (s.to >= to - SLIVER) to = Math.min(to, s.from)
    }

    /*
     * AND WHATEVER THE PERSON DRAGGED.
     *
     * A run fills its wall unless somebody says otherwise, and until now nobody could: tap
     * a wall and you got cabinets corner to corner, which is right for most kitchens and
     * wrong for every one with a fridge alcove, a chimney breast, or a run that simply
     * stops half way along a long wall.
     *
     * `run.span` is that override, in the wall's own millimetres. It is CLAMPED into what
     * the walls and the openings allow rather than replacing them -- a person may shorten a
     * run, never lengthen it past its wall or push it back across a doorway, so no drag can
     * produce a run that could not be built.
     */
    if (run.span) {
      const lo = Math.max(from, Math.min(run.span.from ?? from, L))
      const hi = Math.min(to, Math.max(run.span.to ?? to, 0))
      if (hi - lo >= MIN_RUN) { from = lo; to = hi }
    }

    out[run.id] = { from: Math.min(from, L), to: Math.max(Math.min(to, L), Math.min(from, L)) }
  }
  return out
}

// ── chains ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs of one kind that meet at corners, joined into a chain.
 *
 * This is what lets a cabinet be dragged ROUND a corner: a chain is one rail with bends in
 * it, and a cabinet's position is which leg it is on and how far along. Two runs join when
 * they are the same kind and on consecutive walls of the loop; a run with no neighbour of
 * its kind is a chain of one. Returned in loop order, each chain's legs in loop order.
 */
export function chains(plan) {
  const walls = plan.walls
  const out = []
  for (const kind of ['base', 'wall', 'tall']) {
    const runs = plan.runs.filter((r) => r.kind === kind)
    if (!runs.length) continue
    const byWall = new Map(runs.map((r) => [r.wallId, r]))
    const seen = new Set()
    // Start each chain at a run whose previous wall carries none, so the chain begins at its
    // real beginning; a full ring (a U with all four walls) starts anywhere.
    const starts = walls.filter((w) => byWall.has(w.id) && !byWall.has(prev(walls, w).id))
    const origins = starts.length ? starts : walls.filter((w) => byWall.has(w.id)).slice(0, 1)
    for (const w0 of origins) {
      const legs = []
      let w = w0
      while (w && byWall.has(w.id) && !seen.has(w.id)) {
        seen.add(w.id)
        legs.push(byWall.get(w.id))
        w = next(walls, w)
      }
      if (legs.length) out.push({ kind, legs })
    }

    /*
     * And an island is its own chain of one.
     *
     * `starts` above is derived from the room's walls, so a run on an island appears in no
     * chain at all -- `chainOf` would fall back to a chain of one, which is the right
     * answer, but `design.js` enumerates `chains()` and would therefore never fill it.
     * Being its own chain is also what keeps a cabinet dragged along an island ON it: a
     * chain is the rail, and this rail has one leg.
     */
    for (const run of runs) {
      if (!isIsland(plan, run.wallId)) continue
      if (out.some((c) => c.legs.includes(run))) continue
      out.push({ kind, legs: [run] })
    }
  }
  return out
}

/** The chain a run belongs to, and its index within it. */
export function chainOf(plan, run) {
  for (const c of chains(plan)) {
    const i = c.legs.indexOf(run)
    if (i >= 0) return { chain: c, leg: i }
  }
  return { chain: { kind: run.kind, legs: [run] }, leg: 0 }
}
